import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import getConnectedAccounts from '@/settings/fetch/get-connected-accounts'
import type { ConnectedAccount } from '@/settings/schemas/external-storage-schema'
import { pickVisibleAccount } from '@/settings/utils/pick-visible-account'
import type { RootState } from '@/store/store'
import { getMatterExternalSyncMapping } from '@/user_files/external-storage/fetch/get-matter-external-sync-mapping'
import type { MatterExternalSyncMappingPublicRead } from '@/user_files/external-storage/schemas'

export const kExternalStorageRequestStatus = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
} as const

type RequestStatus = (typeof kExternalStorageRequestStatus)[keyof typeof kExternalStorageRequestStatus]

type MatterSyncRequestState = {
  status: RequestStatus
  error: string | null
  requestId?: string
}

export interface ExternalStorageState {
  accounts: ConnectedAccount[]
  // Preserve a successful result through background refreshes, including an empty account list.
  accountsRequest: MatterSyncRequestState & { hasLoaded?: boolean }
  mappingsByMatterId: Record<string, MatterExternalSyncMappingPublicRead | null>
  mappingRequestsByMatterId: Record<string, MatterSyncRequestState>
}

const createIdleRequestState = (): MatterSyncRequestState => ({
  status: kExternalStorageRequestStatus.IDLE,
  error: null,
})
const idleRequestState = createIdleRequestState()

const initialState: ExternalStorageState = {
  accounts: [],
  accountsRequest: createIdleRequestState(),
  mappingsByMatterId: {},
  mappingRequestsByMatterId: {},
}

type FetchConnectedAccountsArgs = {
  /** Bypass in-flight deduplication after an account write so pre-write data is not kept. */
  force?: boolean
}

export const fetchConnectedAccounts = createAsyncThunk(
  'externalStorage/fetchConnectedAccounts',
  async (_args: FetchConnectedAccountsArgs | void) => {
    const response = await getConnectedAccounts()
    return response.accounts
  },
  {
    // Collapses the duplicate fetches several hooks fire when they mount together: each one's IDLE
    // check still reads IDLE when the next dispatches. Skipping is safe only because the in-flight
    // response is assumed fresh enough for whoever dispatched second. A refetch issued right after
    // a write passes `force`; the fulfilled reducer keeps only the latest request's result.
    condition: (args, { getState }) =>
      Boolean(args?.force) || selectExternalStorageAccountsRequest(getState() as RootState).status !== kExternalStorageRequestStatus.LOADING,
  }
)

type FetchMatterExternalSyncMappingArgs = {
  matterId: string
  /** Bypass in-flight deduplication after a mapping write and discard the pre-write cache. */
  force?: boolean
}

export const fetchMatterExternalSyncMapping = createAsyncThunk(
  'externalStorage/fetchMatterExternalSyncMapping',
  async ({ matterId }: FetchMatterExternalSyncMappingArgs) => {
    const response = await getMatterExternalSyncMapping(matterId)
    return { matterId, mapping: response.mapping }
  },
  {
    condition: ({ matterId, force }: FetchMatterExternalSyncMappingArgs, { getState }) =>
      force || selectMatterExternalSyncMappingRequest(getState() as RootState, matterId).status !== kExternalStorageRequestStatus.LOADING,
  }
)

export const externalStorageSlice = createSlice({
  name: 'externalStorageState',
  initialState,
  reducers: {
    accountDisconnected: (state, action: PayloadAction<{ account: ConnectedAccount }>) => {
      const disconnectedAccount = action.payload.account
      state.accounts = state.accounts.map((account) => (account.id === disconnectedAccount.id ? disconnectedAccount : account))

      for (const [matterId, mapping] of Object.entries(state.mappingsByMatterId)) {
        if (mapping?.account_id === disconnectedAccount.id) {
          delete state.mappingsByMatterId[matterId]
          state.mappingRequestsByMatterId[matterId] = createIdleRequestState()
        }
      }
    },
    nullifyData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectedAccounts.pending, (state, action) => {
        state.accountsRequest = { ...state.accountsRequest, status: kExternalStorageRequestStatus.LOADING, error: null, requestId: action.meta.requestId }
      })
      .addCase(fetchConnectedAccounts.fulfilled, (state, action) => {
        if (state.accountsRequest.requestId !== action.meta.requestId) return
        state.accounts = action.payload
        state.accountsRequest = { status: kExternalStorageRequestStatus.SUCCESS, error: null, hasLoaded: true }
      })
      .addCase(fetchConnectedAccounts.rejected, (state, action) => {
        if (state.accountsRequest.requestId !== action.meta.requestId) return
        state.accountsRequest = {
          ...state.accountsRequest,
          status: kExternalStorageRequestStatus.ERROR,
          error: action.error.message ?? 'Failed to load connected accounts',
        }
      })
      .addCase(fetchMatterExternalSyncMapping.pending, (state, action) => {
        const { matterId, force } = action.meta.arg
        // A post-write refresh must not expose the previous mapping's completed timestamp while
        // the authoritative response is in flight. The request id also fences its older response.
        if (force) delete state.mappingsByMatterId[matterId]
        state.mappingRequestsByMatterId[matterId] = {
          status: kExternalStorageRequestStatus.LOADING,
          error: null,
          requestId: action.meta.requestId,
        }
      })
      .addCase(fetchMatterExternalSyncMapping.fulfilled, (state, action) => {
        if (state.mappingRequestsByMatterId[action.payload.matterId]?.requestId !== action.meta.requestId) return
        state.mappingsByMatterId[action.payload.matterId] = action.payload.mapping
        state.mappingRequestsByMatterId[action.payload.matterId] = { status: kExternalStorageRequestStatus.SUCCESS, error: null }
      })
      .addCase(fetchMatterExternalSyncMapping.rejected, (state, action) => {
        const { matterId } = action.meta.arg
        if (state.mappingRequestsByMatterId[matterId]?.requestId !== action.meta.requestId) return
        state.mappingRequestsByMatterId[matterId] = {
          status: kExternalStorageRequestStatus.ERROR,
          error: action.error.message ?? 'Failed to load matter sync mapping',
        }
      })
  },
})

const selectExternalStorageState = (state: RootState): ExternalStorageState => state.externalStorageState ?? initialState

export const selectConnectedAccounts = (state: RootState) => selectExternalStorageState(state).accounts

export const selectExternalStorageAccountsRequest = (state: RootState) => selectExternalStorageState(state).accountsRequest

export const selectVisibleConnectedExternalAccount = (state: RootState) => {
  const visibleAccount = pickVisibleAccount(selectConnectedAccounts(state))
  return visibleAccount?.status === 'connected' ? visibleAccount : null
}

export const selectMatterExternalSyncMapping = (state: RootState, matterId: string | null | undefined) => {
  if (!matterId) return null
  return selectExternalStorageState(state).mappingsByMatterId[matterId] ?? null
}

/** A cached null is a successful "no synced folder" result, not an unloaded mapping. */
export const selectHasMatterExternalSyncMappingResult = (state: RootState, matterId: string | null | undefined) =>
  !!matterId && selectExternalStorageState(state).mappingsByMatterId[matterId] !== undefined

export const selectMatterExternalSyncMappingRequest = (state: RootState, matterId: string | null | undefined) => {
  if (!matterId) return idleRequestState
  return selectExternalStorageState(state).mappingRequestsByMatterId[matterId] ?? idleRequestState
}

export const selectActiveMatterExternalSyncMapping = (state: RootState, matterId: string | null | undefined) => {
  const mapping = selectMatterExternalSyncMapping(state, matterId)
  return mapping?.status === 'active' ? mapping : null
}

export const ExternalStorageActions = {
  ...externalStorageSlice.actions,
  fetchConnectedAccounts,
  fetchMatterExternalSyncMapping,
}

export default externalStorageSlice.reducer
